import { Plus, X, Video, Link as LinkIcon } from 'lucide-react';
import type { VideoItem } from '../../lib/types';

interface Props {
  value: VideoItem[];
  onChange: (value: VideoItem[]) => void;
}

// Extrae el ID de un video de YouTube de las formas habituales
// (youtu.be/ID, youtube.com/watch?v=ID). Devuelve null si no es YouTube.
function youtubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m?.[1] ?? null;
}

// El `type` es informativo (el renderer del sitio decide el embed por la URL),
// pero lo guardamos coherente: 'youtube' si la URL lo es, 'link' si no.
function detectType(url: string): string {
  return youtubeId(url) ? 'youtube' : 'link';
}

// Editor de lista de videos (recorridos). De momento se usan URLs de YouTube;
// el shape `{title, url, type}` es el que consume apps/site (PropertyDetail).
export function VideoListField({ value, onChange }: Props) {
  const list = Array.isArray(value) ? value : [];

  const update = (i: number, patch: Partial<VideoItem>) =>
    onChange(list.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const setUrl = (i: number, url: string) => update(i, { url, type: detectType(url) });
  const remove = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, { title: '', url: '', type: 'link' }]);

  return (
    <div className="space-y-3">
      {list.map((item, i) => {
        const ytId = youtubeId(item.url || '');
        return (
          <div key={i} className="flex gap-3 rounded-lg border border-ui-border bg-ui-surface p-3">
            {/* Miniatura de YouTube si la URL es válida */}
            <div className="shrink-0 w-28 aspect-video rounded-md overflow-hidden bg-ui-bg flex items-center justify-center text-ui-text-subtle">
              {ytId ? (
                <img
                  src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : item.url ? (
                <LinkIcon size={18} />
              ) : (
                <Video size={18} />
              )}
            </div>

            <div className="flex-1 space-y-2 min-w-0">
              <input
                type="url"
                value={item.url ?? ''}
                onChange={(e) => setUrl(i, e.target.value)}
                placeholder="https://youtu.be/… o https://youtube.com/watch?v=…"
                className="w-full bg-ui-bg border border-ui-border rounded-md px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:border-ui-accent focus:ring-1 focus:ring-ui-accent/30 placeholder:text-ui-text-subtle transition-colors"
              />
              <input
                type="text"
                value={item.title ?? ''}
                onChange={(e) => update(i, { title: e.target.value })}
                placeholder="Título (opcional) — p. ej. «Recorrido completo»"
                className="w-full bg-ui-bg border border-ui-border rounded-md px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:border-ui-accent focus:ring-1 focus:ring-ui-accent/30 placeholder:text-ui-text-subtle transition-colors"
              />
              {item.url && !ytId && (
                <p className="text-sm text-ui-text-subtle">
                  No parece una URL de YouTube — se mostrará como enlace, no como video embebido.
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => remove(i)}
              title="Quitar video"
              className="self-start p-1.5 text-ui-text-muted hover:text-ui-danger rounded transition-colors"
            >
              <X size={15} />
            </button>
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-ui-text-muted hover:text-ui-text border border-dashed border-ui-border hover:border-ui-border-strong rounded-md transition-colors"
      >
        <Plus size={13} /> Añadir video
      </button>
    </div>
  );
}
